import { clsx } from 'clsx';
import { AudioManager } from 'engine/lib/AudioManager';
import { ArrowIcon } from 'game/icons/ArrowIcon';
import { nonEmpty } from 'game/lib/NonEmpty';
import { createKeyframes, createStyles } from 'game/lib/createStyles';
import { rand } from 'game/lib/rand';
import { recurseNodes } from 'game/lib/recurseNodes';
import random from 'lodash/random';
import React, { createRef } from 'react';
import { DialogMessage } from './DialogMessage';

/* eslint-disable global-require */
const plops = nonEmpty([
  new AudioManager(require('game/sfx/plops-01.mp3')),
  new AudioManager(require('game/sfx/plops-02.mp3')),
  new AudioManager(require('game/sfx/plops-03.mp3')),
]);
/* eslint-enable global-require */

const float = createKeyframes('float', {
  '0%': { transform: 'translateY(-3px)' },
  '100%': { transform: 'translateY(3px)' },
});

const classes = createStyles({
  hide: { display: 'none' },
  arrow: {
    position: 'absolute',
    right: 40,
    bottom: 7,
    opacity: 0,
    transition: 'opacity 100ms linear',
    fill: 'currentColor',
  },
  arrowVisible: {
    opacity: 1,
    animation: `1.2s ease-in-out infinite alternate ${float}`,
  },
});

interface DialogMessageUIProps {
  message: DialogMessage,
  hasNext: boolean,
  advance: () => void,
}

export class DialogMessageUI extends React.Component<DialogMessageUIProps> {

  charIndex = -1;

  spans: HTMLSpanElement[] = [];

  messageContainer = createRef<HTMLDivElement>();
  messageSource = createRef<HTMLDivElement>();

  plop: null | AudioManager = null;

  onClick = () => {
    this.next();
  };

  onKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'Space' && !e.repeat) {
      this.next();
    }
  };

  next() {
    if (this.charIndex === this.spans.length) {
      this.props.advance();
    } else {
      for (let i = this.charIndex; i < this.spans.length; i++) {
        this.spans[i].style.opacity = '1';
      }
      this.charIndex = this.spans.length;
    }
  }

  startMessageAnimation() {
    this.spans = [];
    this.charIndex = 0;
    const clone = this.messageSource.current!.cloneNode(true);
    recurseNodes(clone, node => {
      if (node instanceof Text && node.textContent) {
        const chars = node.textContent.split('');
        const nodes = chars.map(char => {
          const span = document.createElement('span');
          span.style.opacity = '0';
          span.textContent = char;
          return span;
        });
        node.replaceWith(...nodes);
        this.spans.push(...nodes);
      }
    });
    this.messageContainer.current?.replaceChildren(...Array.from(clone.childNodes));
  }

  componentDidMount() {
    window.addEventListener('click', this.onClick);
    window.addEventListener('keydown', this.onKeyDown);
    this.startMessageAnimation();
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.onClick);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  componentDidUpdate(prevProps: DialogMessageUIProps) {
    if (this.props.message === prevProps.message) {
      const span = this.spans[this.charIndex];
      if (span) {
        span.style.opacity = '1';
        this.charIndex++;
        if (!this.plop || !this.plop.playing) {
          this.plop = rand(plops).clone({ volume: random(0.5, 0.8), playbackRate: random(0.8, 1.1) });
          this.plop.play();
        }
      }
      return;
    }
    this.startMessageAnimation();
  }

  render() {
    return (
      <>
        <div ref={this.messageSource} className={classes.hide}>
          {this.props.message.text}
        </div>
        <div ref={this.messageContainer} />
        <div className={clsx(classes.arrow, {
          [classes.arrowVisible]: this.props.hasNext,
        })}
        >
          <ArrowIcon />
        </div>
      </>
    );
  }
}
